import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Conect from '../../services/api/Conect';
import NotificationsAlert from '../../services/NotificationsAlert';

const INITIAL_FORM = (vehicle) => ({
  plate: vehicle?.plate || '',
  brand: vehicle?.brand_id || '',
  model: vehicle?.model_id || '',
  year: vehicle?.year || '',
  type: vehicle?.type || '',
  description: vehicle?.description || '',
});

const getToken = () => localStorage.getItem('fptoken');

const normalizeText = (text = '') => text.trim().replace(/\s+/g, ' ');
const normalizePlate = (value = '') => value.toUpperCase().trim().replace(/\s+/g, ' ');

export default function VehicleForm({ vehicles, vehicle, onClose, onRefresh }) {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => INITIAL_FORM(vehicle));

  // Gera anos apenas uma vez
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: current - 1979 }, (_, i) => String(current - i));
  }, []);

  // Busca marcas uma vez ao montar
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const { data } = await Conect.get(`/listallbrands${getToken()}`);
        if (!data.erro) setBrands(data.data);
      } catch (err) {
        console.error('Erro ao buscar marcas:', err);
      }
    };
    fetchBrands();
  }, []);

  // Busca modelos quando a marca muda
  const fetchModels = useCallback(async (brandId) => {
    try {
      const { data } = await Conect.post(`/listmodelwherebrand${getToken()}`, {
        brand_id: Number(brandId),
      });
      if (!data.erro) setModels(data.data);
    } catch (err) {
      console.error('Erro ao buscar modelos:', err);
    }
  }, []);

  useEffect(() => {
    if (form.brand) {
      fetchModels(form.brand);
    } else {
      setModels([]);
    }
  }, [form.brand, fetchModels]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setForm(prev => {
      if (name === 'plate') return { ...prev, plate: normalizePlate(value) };
      if (name === 'brand') return { ...prev, brand: value, model: '' };
      return { ...prev, [name]: value };
    });
  }, []);

  // Valida usando Set para lookup O(1)
  const plateSet = useMemo(
    () => new Set(vehicles.map(v => v.plate)),
    [vehicles]
  );

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    const warn = (msg) => { NotificationsAlert('warning', '', msg); return false; };

    if (!form.plate)        return warn('Informe a identificação do veículo');
    // Ignora placa do próprio veículo em edição
    if (plateSet.has(form.plate) && form.plate !== vehicle?.plate)
      return warn('Veículo já cadastrado. Verifique na lista.');
    if (!form.brand)        return warn('Selecione a marca');
    if (!form.model)        return warn('Selecione o modelo');
    if (!form.year)         return warn('Selecione o ano');
    if (!form.type)         return warn('Selecione o tipo');

    const payload = {
      plate:       normalizePlate(form.plate),
      brand:       Number(form.brand),
      model:       Number(form.model),
      year:        form.year,
      type:        normalizeText(form.type),
      description: normalizeText(form.description),
      id_brand:    Number(form.brand),
      id_model:    Number(form.model),
    };

    try {
      setLoading(true);

      const response = vehicle
        ? await Conect.put(`/updatevehicle/${vehicle.id}${getToken()}`, payload)
        : await Conect.post(`/createvehicle/${getToken()}`, payload);

      if ([200, 201].includes(response?.status) && !response?.data?.erro) {
        NotificationsAlert('success', '', response?.data?.msg || 'Veículo salvo com sucesso');
        onRefresh();
        onClose();
      }
    } catch (err) {
      console.error('Erro ao salvar veículo:', err?.response?.data || err);
      NotificationsAlert(
        'danger',
        'Erro ao salvar veículo',
        err?.response?.data?.msg || err?.message || 'Erro interno da aplicação'
      );
    } finally {
      setLoading(false);
    }
  }, [loading, form, plateSet, vehicle, onRefresh, onClose]);

  return (
    <div className="v-form">
      <h3>{vehicle ? 'Editar Veículo' : 'Cadastrar Veículo'}</h3>

      <div className="v-form-row">
        <input
          name="plate"
          placeholder="Placa / Identificação"
          value={form.plate}
          onChange={handleChange}
        />

        <select name="brand" value={form.brand} onChange={handleChange}>
          <option value="">Marca</option>
          {brands.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        <select name="model" value={form.model} onChange={handleChange} disabled={!form.brand}>
          <option value="">Modelo</option>
          {models.map(m => (
            <option key={m.id} value={m.id}>{m.description}</option>
          ))}
        </select>

        <select name="year" value={form.year} onChange={handleChange}>
          <option value="">Ano</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select name="type" value={form.type} onChange={handleChange}>
          <option value="">Tipo</option>
          <option value="VEICULO">VEICULO</option>
          <option value="SEMI-REBOQUE">SEMI-REBOQUE</option>
        </select>
      </div>

      <textarea
        name="description"
        placeholder="Descrição (opcional)"
        value={form.description}
        onChange={handleChange}
      />

      <div className="v-actions">
        <button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Salvando…' : vehicle ? 'Salvar Alterações' : 'Cadastrar'}
        </button>
        <button className="v-cancel-btn" onClick={onClose} disabled={loading}>
          Cancelar
        </button>
      </div>
    </div>
  );
}